import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute, PublicOnlyRoute } from './components/auth/ProtectedRoute';
import { LandingPage } from './views/landing/LandingPage';
import { UseCasePage } from './views/landing/UseCasePage';
import { PricingPage } from './views/landing/PricingPage';
import { FaqPage } from './views/landing/FaqPage';
import { DocsPage } from './views/landing/docs/DocsPage';
import { TemplatesPage } from './views/landing/TemplatesPage';
import { Studio } from './views/Studio';
import { VideoListView } from './views/video/VideoListView';
import { VideoStudio } from './views/video/VideoStudio';
import { ScheduleView } from './views/video/ScheduleView';
import { SocialConnectCallback } from './views/video/SocialConnectCallback';
import { VideoErrorBoundary } from './views/video/videoUi';

import { LoginView } from './views/auth/LoginView';
import { SignUpView } from './views/auth/SignUpView';
import { ForgotPasswordView } from './views/auth/ForgotPasswordView';
import { ResetPasswordView } from './views/auth/ResetPasswordView';
import { OAuthCallbackView } from './views/auth/OAuthCallbackView';
import { TermsView } from './views/pages/TermsView';
import { PrivacyView } from './views/pages/PrivacyView';
import { DataDeletionView } from './views/pages/DataDeletionView';
import { AboutView } from './views/pages/AboutView';
import { WebContainerConnect } from './views/WebContainerConnect';
import { AccountLayout } from './views/account/AccountLayout';
import { MyProjectsView } from './views/account/MyProjectsView';
import { BillingView } from './views/account/BillingView';
import { ProfileSettingsView } from './views/account/ProfileSettingsView';
import { ReceiptView } from './views/account/ReceiptView';
import { AffiliateView } from './views/account/AffiliateView';
import { DashboardLayout } from './views/dashboard/DashboardLayout';
import { DashHome } from './views/dashboard/DashHome';
import { DashProjects } from './views/dashboard/DashProjects';
import { DashTrash } from './views/dashboard/DashTrash';
import { DashMedia } from './views/dashboard/DashMedia';
import { DashTemplates } from './views/dashboard/DashTemplates';
import { DashScheduler } from './views/dashboard/DashScheduler';
import { DashAnalytics } from './views/dashboard/DashAnalytics';
import { DashIntegration } from './views/dashboard/DashIntegration';
import { GoogleAnalytics } from './components/analytics/GoogleAnalytics';
import { ToastProvider } from './components/common/Toast';
import { ConfirmProvider } from './components/common/ConfirmDialog';

function App() {
    // NOTE: published apps are no longer rendered by this SPA. They are real
    // static sites (built from each app's workspace) served by tenant-api at
    // {subdomain}.hyvay.com — Caddy routes those hosts away from us entirely.
    return (
        <AuthProvider>
        <ToastProvider>
        <ConfirmProvider>
            <GoogleAnalytics />
            <Helmet>
                <title>HyVay - Build your app easily</title>
                <meta name="description" content="HyVay - Build professional web apps without coding. The best active No-Code App Builder in Indonesia." />
                <meta property="og:title" content="HyVay - Build your app easily" />
                <meta property="og:description" content="HyVay - Build professional web apps without coding. The best active No-Code App Builder in Indonesia." />
                <meta property="twitter:title" content="HyVay - Build your app easily" />
                <meta property="twitter:description" content="HyVay - Build professional web apps without coding. The best active No-Code App Builder in Indonesia." />
            </Helmet>
            <Routes>
                {/* Landing page (design: "HyVay Landing Page"). Public; the
                    workspace section and credits appear once signed in. */}
                <Route
                    path="/"
                    element={<LandingPage />}
                />
                <Route
                    path="/studio/:tenantId"
                    element={
                        <ProtectedRoute>
                            <Studio />
                        </ProtectedRoute>
                    }
                />
                {/* Dashboard (signed-in home): replaces /account/projects, the /video list and /video/schedule. */}
                <Route
                    path="/dashboard"
                    element={
                        <ProtectedRoute>
                            <DashboardLayout />
                        </ProtectedRoute>
                    }
                >
                    <Route index element={<DashHome />} />
                    <Route path="projects" element={<DashProjects />} />
                    <Route path="trash" element={<DashTrash />} />
                    <Route path="media" element={<DashMedia />} />
                    <Route path="templates" element={<DashTemplates />} />
                    <Route path="scheduler" element={<DashScheduler />} />
                    <Route path="analytics" element={<DashAnalytics />} />
                    <Route path="integration" element={<DashIntegration />} />
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Route>
                {/* Video maker: the list and schedule moved into the dashboard. */}
                <Route path="/video" element={<Navigate to="/dashboard/projects?type=video" replace />} />
                <Route path="/video/schedule" element={<Navigate to="/dashboard/scheduler" replace />} />
                {/* OAuth return from YouTube / TikTok / LinkedIn / Meta (social posting). */}
                <Route
                    path="/video/connect/:provider"
                    element={
                        <ProtectedRoute>
                            <SocialConnectCallback />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/video/:id"
                    element={
                        <ProtectedRoute>
                            <VideoErrorBoundary key="studio"><VideoStudio /></VideoErrorBoundary>
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="/account"
                    element={
                        <ProtectedRoute>
                            <AccountLayout />
                        </ProtectedRoute>
                    }
                >
                    <Route index element={<Navigate to="profile" replace />} />
                    <Route path="profile" element={<ProfileSettingsView />} />
                    <Route path="projects" element={<Navigate to="/dashboard/projects" replace />} />
                    <Route path="billing" element={<BillingView />} />
                    <Route path="affiliate" element={<AffiliateView />} />
                </Route>
                {/* Receipts render standalone (print-friendly), outside the sidebar shell. */}
                <Route
                    path="/account/billing/receipt/:orderId"
                    element={
                        <ProtectedRoute>
                            <ReceiptView />
                        </ProtectedRoute>
                    }
                />
                {/* Old links and bookmarks keep working. */}
                <Route path="/profile" element={<Navigate to="/account/profile" replace />} />
                <Route path="/profile/projects" element={<Navigate to="/account/projects" replace />} />
                <Route path="/profile/invoice" element={<Navigate to="/account/billing" replace />} />
                <Route
                    path="/login"
                    element={
                        <PublicOnlyRoute>
                            <LoginView />
                        </PublicOnlyRoute>
                    }
                />
                <Route
                    path="/signup"
                    element={
                        <PublicOnlyRoute>
                            <SignUpView />
                        </PublicOnlyRoute>
                    }
                />
                <Route
                    path="/forgot-password"
                    element={
                        <PublicOnlyRoute>
                            <ForgotPasswordView />
                        </PublicOnlyRoute>
                    }
                />
                <Route
                    path="/reset-password"
                    element={
                        <PublicOnlyRoute>
                            <ResetPasswordView />
                        </PublicOnlyRoute>
                    }
                />
                <Route path="/auth/callback" element={<OAuthCallbackView />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/faq" element={<FaqPage />} />
                <Route path="/templates" element={<TemplatesPage />} />
                <Route path="/docs" element={<Navigate to="/docs/introduction" replace />} />
                <Route path="/docs/:slug" element={<DocsPage />} />
                <Route path="/use-cases/:slug" element={<UseCasePage />} />
                <Route path="/use-cases" element={<Navigate to="/use-cases/conference" replace />} />
                <Route path="/about" element={<AboutView />} />
                <Route path="/terms" element={<TermsView />} />
                <Route path="/privacy" element={<PrivacyView />} />
                <Route path="/data-deletion" element={<DataDeletionView />} />
                {/* WebContainer handshake for opening the dev preview in a new tab —
                    must exist or the catch-all redirect breaks the connection. */}
                <Route path="/webcontainer/connect/*" element={<WebContainerConnect />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </ConfirmProvider>
        </ToastProvider>
        </AuthProvider>
    );
}

export default App;
